import styled from 'styled-components';
import { useEffect, type ReactNode } from 'react';
import { LightBorderButton, StyledButton } from './Button';
import { useSidebar } from '../contexts/sidebar';

interface ICommonFooterStyleProps {
  type?: 'info' | 'black' | 'green' | 'success';
  theme: any;
}

const CommonFooterStyle = styled.div<ICommonFooterStyleProps>`
  background: ${({ type, theme }) =>
    type === 'info'
      ? theme.colors.primaryLight
      : type === 'black'
        ? theme.colors.primaryLight
        : type === 'green'
          ? theme.colors.green
          : theme.colors.success};
  color: ${({ theme }) => theme.colors.white};
  padding: 1.5em 0;
  position: fixed;
  bottom: 0;
  margin: 0;
  text-align: center;
  font-size: 15px;
  line-height: 18px;
  z-index: 10;
`;

interface IFooterRowProps {
  isCollapsed?: boolean;
  fullWidth?: boolean;
}

const FooterRow = styled(CommonFooterStyle)<IFooterRowProps>`
  transition: all 0.2s;
  left: 240px;
  width: calc(100% - 240px);
  z-index: 1000;
`;

const FooterHeader = styled.div`
  font-size: 20px;
  line-height: 23px;
  font-weight: bold;
`;

const FooterContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;

  @media only screen and (max-width: 768px) {
    padding-left: 15px !important;
    padding-right: 15px !important;

    .footer-title {
      font-size: 16px;
      text-align: left;
    }

    &.assign-items {
      align-items: flex-end;
    }
  }
`;

interface IFooterAlertProps {
  type?: 'info' | 'black' | 'green' | 'success';
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

function FooterAlert({ type, children, fullWidth }: IFooterAlertProps) {
  const { setFooterActive } = useSidebar();

  useEffect(() => {
    setFooterActive(true);
    return () => setFooterActive(false);
  }, [setFooterActive]);

  return (
    <FooterRow type={type} fullWidth={fullWidth}>
      {children}
    </FooterRow>
  );
}

interface IAlertFooterBarProps {
  cancel: () => void;
  cancelButtonText?: string;
  disabled?: boolean;
  loading?: boolean;
  message: string;
  save?: () => void;
  saveButtonText?: string;
  submitButton?: boolean;
  disabledSaveButton?: boolean;
}

export function AlertFooterBar({
  cancel,
  cancelButtonText = 'Discard',
  disabled,
  loading = false,
  message,
  save,
  saveButtonText = 'Save Changes',
  submitButton,
  disabledSaveButton = false,
}: IAlertFooterBarProps) {
  return (
    <FooterAlert type="green">
      <FooterContainer className="justify-content-between px-4">
        <FooterHeader className="m-0 footer-title">{message}</FooterHeader>
        <div className="d-flex align-items-center">
          <LightBorderButton disabled={disabled} type="button" onClick={cancel}>
            {cancelButtonText}
          </LightBorderButton>
          <StyledButton
            {...(submitButton ? {} : { onClick: save })}
            className="ml-3 save-action"
            disabled={disabled || disabledSaveButton}
            loading={loading}
            type={submitButton ? 'submit' : 'button'}
          >
            {saveButtonText}
          </StyledButton>
        </div>
      </FooterContainer>
    </FooterAlert>
  );
}
